import { useTranslation } from "react-i18next";
import {
  CButton,
  CButtonGroup,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
} from "@coreui/react-pro";
import type { PortfolioInstrumentGroupDto } from "../../../api/hooks/usePortfolioBuilder";
import { useAssetClasses, useReferenceValueLabel } from "../../../api/hooks/useReference";
import { SIZES } from "../portfolio-builder";

interface BuilderControlsProps {
  size: number;
  onSizeChange: (size: number) => void;
  /** Whether any portfolio of this many strategies is left with the instruments chosen. */
  sizeAvailable: (size: number) => boolean;
  groups: PortfolioInstrumentGroupDto[];
  exclude: number;
  onToggleGroup: (index: number) => void;
  showPath: boolean;
  onShowPathChange: (show: boolean) => void;
  pathDrawn: boolean;
  selectedDrawn: boolean;
  otherCount: number;
}

/**
 * How many strategies and which instruments, between the questions and the dots. Neither is a
 * two-answer question, so they are controls the customer can change at any point.
 */
export function BuilderControls({
  size,
  onSizeChange,
  sizeAvailable,
  groups,
  exclude,
  onToggleGroup,
  showPath,
  onShowPathChange,
  pathDrawn,
  selectedDrawn,
  otherCount,
}: BuilderControlsProps) {
  const { t } = useTranslation("portfolioBuilder");
  const assetClassLabel = useReferenceValueLabel(useAssetClasses().data);

  const groupName = (group: PortfolioInstrumentGroupDto) =>
    group.assetClass ? assetClassLabel(group.assetClass) : t("controls.otherInstruments");
  const leftOut = groups.filter((_, index) => exclude & (1 << index));
  const summary =
    leftOut.length === 0
      ? t("controls.allInstruments")
      : leftOut.length === 1
        ? t("controls.leavingOutOne", { group: groupName(leftOut[0]).toLocaleLowerCase() })
        : t("controls.leavingOut", { count: leftOut.length });

  return (
    <div className="portfolio-controls">
      <span className="portfolio-control">
        {t("controls.strategies")}
        <CButtonGroup size="sm" className="btn-group-pill" role="group" aria-label={t("controls.strategiesLabel")}>
          {SIZES.map((option) => (
            <CButton
              key={option}
              color="primary"
              variant={size === option ? undefined : "outline"}
              aria-pressed={size === option}
              disabled={!sizeAvailable(option)}
              onClick={() => onSizeChange(option)}
            >
              {option}
            </CButton>
          ))}
        </CButtonGroup>
      </span>

      <span className="portfolio-control">
        {t("controls.instruments")}
        <CDropdown
          autoClose="outside"
          popperConfig={(config) => ({
            ...config,
            modifiers: [...(config.modifiers ?? []), { name: "preventOverflow", options: { padding: 8 } }],
          })}
        >
          <CDropdownToggle
            size="sm"
            shape="rounded-pill"
            color={exclude ? "primary" : "secondary"}
            variant="outline"
            className="portfolio-instruments-toggle"
          >
            {summary}
          </CDropdownToggle>
          <CDropdownMenu as="div" className="portfolio-instruments-menu">
            {groups.map((group, index) => (
              <button
                key={group.assetClass ?? "other"}
                type="button"
                className="portfolio-instrument"
                aria-pressed={!(exclude & (1 << index))}
                onClick={() => onToggleGroup(index)}
              >
                <span className="portfolio-instrument-box" aria-hidden />
                <span className="portfolio-instrument-name">{groupName(group)}</span>
                <small className="portfolio-instrument-symbols">{group.symbols.join(" · ")}</small>
              </button>
            ))}
            <p className="portfolio-instruments-note">{t("controls.untick")}</p>
          </CDropdownMenu>
        </CDropdown>
      </span>

      <span className="portfolio-control">
        <CButton
          size="sm"
          color="primary"
          variant="outline"
          className="portfolio-path-toggle"
          aria-pressed={showPath}
          onClick={() => onShowPathChange(!showPath)}
        >
          {showPath ? t("controls.hidePath") : t("controls.showPath")}
        </CButton>
      </span>

      {/* Every key holds its place whether or not it is drawn, so the row never reflows. */}
      <span className="portfolio-legend">
        <span>
          <i className="portfolio-key-pick" />
          {t("dots.yourMatch")}
        </span>
        <span className={pathDrawn ? undefined : "is-off"}>
          <i className="portfolio-key-path" />
          {t("dots.yourPath")}
        </span>
        <span className={selectedDrawn ? undefined : "is-off"}>
          <i className="portfolio-key-selected" />
          {t("dots.selected")}
        </span>
        <span>
          <i className="portfolio-key-dot" />
          {t("dots.others", { count: otherCount })}
        </span>
      </span>
    </div>
  );
}
